import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  FileText,
  Paperclip,
  X,
  type LucideIcon,
} from 'lucide-react';
import type { ChargeStatus, ExpenseCategory, TicketStatus } from '@/api/types';
import { UPLOAD_ALLOWED_TYPES, UPLOAD_MAX_BYTES } from '@/api/types';
import { formatMoney, formatNumber } from '@/lib/format';
import { useLanguage } from '@/lib/useLocalizedName';

/* ------------------------------------------------ directional chevrons */

/** "Forward" points the way reading goes: left in Arabic, right in English. */
export function ChevronForward({ size = 16 }: { size?: number }) {
  const { i18n } = useTranslation();
  const Icon = i18n.language === 'ar' ? ChevronLeft : ChevronRight;
  return <Icon size={size} />;
}

export function ChevronBack({ size = 16 }: { size?: number }) {
  const { i18n } = useTranslation();
  const Icon = i18n.language === 'ar' ? ChevronRight : ChevronLeft;
  return <Icon size={size} />;
}

/* ------------------------------------------------------------- money */

export function MoneyText({ amount, className }: { amount: string; className?: string }) {
  const language = useLanguage();
  return <span className={className ?? 'number'}>{formatMoney(amount, language)}</span>;
}

/* -------------------------------------------------------- status chip */

export const chargeStatusClass: Record<ChargeStatus, string> = {
  paid: 'status-paid',
  late: 'status-late',
  partial: 'status-partial',
  unpaid: 'status-unpaid',
  // معفى: ليس متأخراً ولا مدفوعاً. حياديّ عمداً حتى لا يُقرأ كإنجاز
  // تحصيل ولا كتقصير.
  exempt: 'status-exempt',
};

export const ticketStatusClass: Record<TicketStatus, string> = {
  new: 'status-ticket',
  inProgress: 'status-partial',
  closed: 'status-paid',
};

export function StatusChip({ status }: { status: ChargeStatus }) {
  const { t } = useTranslation();
  return (
    <span className={`status-badge ${chargeStatusClass[status]}`}>
      {status === 'paid' ? <Check size={13} /> : status === 'late' ? <AlertCircle size={13} /> : null}
      {t(`payment.statuses.${status}`)}
    </span>
  );
}

export function TicketChip({ status }: { status: TicketStatus }) {
  const { t } = useTranslation();
  return (
    <span className={`status-badge ${ticketStatusClass[status]}`}>
      {status === 'closed' ? <Check size={13} /> : status === 'inProgress' ? <Clock size={13} /> : null}
      {t(`ticket.statuses.${status}`)}
    </span>
  );
}

/** Colour is status everywhere — the categories share one palette too. */
export const categoryColors: Record<ExpenseCategory, string> = {
  salaries: '#4a6cf7',
  water: '#2f8fd8',
  water_tank: '#1f6f9e',
  electricity: '#d9a441',
  sewage: '#7a6a52',
  utilities: '#8e7cd8',
  cleaning: '#177a58',
  maintenance: '#d98ba0',
  bank_fee: '#b05c8e',
  transport: '#3f9e8c',
  // اشتراك المنصّة رمادي عمداً: ليس بنداً تشغيلياً للعمارة، وتلوينه
  // كبقية البنود يجعله ينافسها على الانتباه في المخطّط الدائري.
  platform_fee: '#9895a8',
  other: '#8a5510',
};

export function CategoryLabel({ category }: { category: ExpenseCategory }) {
  const { t } = useTranslation();
  return (
    <span className="category-label">
      <i className="category-dot" style={{ background: categoryColors[category] }} />
      {t(`expense.categories.${category}`)}
    </span>
  );
}

/* -------------------------------------------------------- page header */

export function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
}) {
  return (
    <div className="page-intro">
      <div>
        <h2 className="font-display" style={{ margin: 0, fontSize: 18, letterSpacing: '-.04em' }}>
          {title}
        </h2>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function SectionHead({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
}) {
  return (
    <div className="section-head">
      <div>
        <h2>{title}</h2>
        {subtitle && <p className="section-sub">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

/* -------------------------------------------------------------- modal */

export function Modal({
  title,
  children,
  onClose,
  locked = false,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  /** #64: نافذة لا تُغلق إلا من داخلها — لا X ولا Escape ولا نقرة خارجها. */
  locked?: boolean;
}) {
  const { t } = useTranslation();

  /**
   * Rendered through a portal, not in place.
   *
   * This is the fix for the dialog that kept appearing pinned to the bottom of
   * the page: `position: fixed` is measured against the nearest ancestor that
   * has a transform, filter or animation, not against the viewport. Several
   * page containers animate on entry, so the backdrop was being positioned
   * inside them. Mounting on `document.body` removes every such ancestor, so
   * the dialog lands in the middle of whatever the user is actually looking
   * at, wherever they have scrolled to.
   */
  useEffect(() => {
    // Freeze the page behind the dialog so a scroll gesture does not move it.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !locked) onClose();
    };
    window.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose, locked]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      // #99: النقر خارج النافذة لا يغلقها — كان يُضيّع ما كُتب بنقرة عابرة على
      // الهاتف. الإغلاق من X أو «إلغاء» أو Escape فقط.
      onClick={undefined}
    >
      <div className="modal">
        <div className="modal-head">
          <h2>{title}</h2>
          {!locked && (
            <button type="button" className="icon-button" onClick={onClose} aria-label={t('common.close')}>
              <X size={18} />
            </button>
          )}
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}

export function ConfirmDialog({
  title,
  message,
  confirmLabel,
  destructive = false,
  onConfirm,
  onCancel,
}: {
  title: string;
  message: string;
  confirmLabel?: string;
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Modal title={title} onClose={onCancel}>
      <p className="modal-body">{message}</p>
      <div className="form-actions">
        <button type="button" className="button outline" onClick={onCancel}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className={destructive ? 'button danger' : 'button'}
          onClick={onConfirm}
        >
          {confirmLabel ?? t('common.confirm')}
        </button>
      </div>
    </Modal>
  );
}

/* --------------------------------------------------- empty / loading */

export function EmptyState({
  icon: Icon = FileText,
  title,
  body,
  action,
}: {
  icon?: LucideIcon;
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <span className="empty-icon">
        <Icon size={22} />
      </span>
      <h3>{title}</h3>
      <p>{body}</p>
      {action}
    </div>
  );
}

export function Skeleton({ height = 128, className = '' }: { height?: number; className?: string }) {
  return <div className={`skeleton ${className}`} style={{ height }} aria-hidden="true" />;
}

export function ErrorAlert({ message }: { message: string }) {
  return (
    <div className="alert-error" role="alert">
      <AlertCircle size={16} />
      <span>{message}</span>
    </div>
  );
}

/* ═══════════════════════════════════════════════ pagination + attachments */

/**
 * Server-side paging control. Deliberately page-number based rather than
 * infinite scroll: a manager reconciling a month needs to know where they are
 * and be able to go back to it, and the API returns an exact `total`.
 *
 * Arrows are the logical `ChevronBack`/`ChevronForward` pair, so the control
 * flips with the document direction without a single conditional.
 */
export function Pagination({
  page,
  pageSize,
  total,
  onChange,
  busy = false,
}: {
  page: number;
  pageSize: number;
  total: number;
  onChange: (page: number) => void;
  busy?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.language.startsWith('ar') ? 'ar' : 'en';
  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  // One page of results needs no control at all.
  if (total <= pageSize) return null;

  const first = (page - 1) * pageSize + 1;
  const last = Math.min(total, page * pageSize);

  return (
    <nav className="pagination" aria-label={t('common.pagination')}>
      <span className="pagination__range">
        {t('common.showingRange', {
          first: formatNumber(first, language),
          last: formatNumber(last, language),
          total: formatNumber(total, language),
        })}
      </span>

      <div className="pagination__controls">
        <button
          type="button"
          aria-label={t('common.previousPage')}
          disabled={busy || page <= 1}
          onClick={() => onChange(page - 1)}
        >
          <ChevronBack size={16} />
        </button>
        <span aria-live="polite">
          {t('common.pageOf', {
            page: formatNumber(page, language),
            pages: formatNumber(lastPage, language),
          })}
        </span>
        <button
          type="button"
          aria-label={t('common.nextPage')}
          disabled={busy || page >= lastPage}
          onClick={() => onChange(page + 1)}
        >
          <ChevronForward size={16} />
        </button>
      </div>
    </nav>
  );
}

/**
 * Receipt / photo picker. Validates size and type before the request so the
 * user gets a translated error instantly instead of a 413 after uploading 8MB
 * over mobile data — the server validates again, and that check is the real one.
 */
export function AttachmentPicker({
  onSelect,
  disabled = false,
  label,
}: {
  onSelect: (file: File) => void;
  disabled?: boolean;
  label?: string;
}) {
  const { t } = useTranslation();
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handle = (file: File | undefined) => {
    if (!file) return;
    if (file.size > UPLOAD_MAX_BYTES) {
      setError(t('upload.tooLarge'));
      return;
    }
    if (!UPLOAD_ALLOWED_TYPES.includes(file.type)) {
      setError(t('upload.badType'));
      return;
    }
    setError('');
    onSelect(file);
  };

  return (
    <div className="attachment-picker">
      <input
        ref={inputRef}
        type="file"
        accept={UPLOAD_ALLOWED_TYPES.join(',')}
        hidden
        disabled={disabled}
        onChange={(event) => {
          handle(event.target.files?.[0]);
          // Allow re-picking the same file after a failed attempt.
          event.target.value = '';
        }}
      />
      <button
        type="button"
        className="button ghost"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
      >
        <Paperclip size={16} aria-hidden /> {label ?? t('upload.attach')}
      </button>
      {error && <span className="attachment-picker__error">{error}</span>}
    </div>
  );
}
